import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Tv, Play, Shield, Globe, ChevronRight, Zap, Clock } from "lucide-react";
import axios from "axios";
import { brand, brandId } from "../brand.config";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ── Decorative SVG: concentric arcs + subtle grid (ArcPlayer only) ── */
const ArcDecoration = () => (
  <svg
    className="absolute inset-0 w-full h-full pointer-events-none"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <circle cx="50%" cy="-5%" r="52%" fill="none" stroke="rgba(0,191,255,0.06)" strokeWidth="1" />
    <circle cx="50%" cy="-5%" r="64%" fill="none" stroke="rgba(0,191,255,0.04)" strokeWidth="1" />
    <circle cx="50%" cy="-5%" r="76%" fill="none" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
    <line x1="0" y1="33%" x2="100%" y2="33%" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
    <line x1="0" y1="66%" x2="100%" y2="66%" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
    <line x1="25%" y1="0" x2="25%" y2="100%" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
    <line x1="50%" y1="0" x2="50%" y2="100%" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
    <line x1="75%" y1="0" x2="75%" y2="100%" stroke="rgba(0,191,255,0.025)" strokeWidth="1" />
  </svg>
);

/* ── Decorative SVG: pulse waveform at the bottom (PulsePlayer only) ── */
const PulseDecoration = () => (
  <svg
    className="absolute bottom-0 left-0 right-0 pointer-events-none"
    height="100"
    viewBox="0 0 1200 100"
    preserveAspectRatio="none"
    aria-hidden="true"
    style={{ opacity: 0.12 }}
  >
    <defs>
      <linearGradient id="pulseGrad" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="#00E5FF" />
        <stop offset="100%" stopColor="#A855F7" />
      </linearGradient>
    </defs>
    <polyline
      points="0,50 80,50 120,15 170,85 220,30 280,70 330,20 390,65 440,45 500,8 560,50 620,78 680,22 740,62 800,18 860,70 910,35 960,50 1040,50 1090,25 1140,72 1180,42 1200,50"
      fill="none"
      stroke="url(#pulseGrad)"
      strokeWidth="2.5"
    />
  </svg>
);

const LandingPage = () => {
  const navigate = useNavigate();
  const isNettv = brandId === "nettv";
  const isArc = brand.isArc;
  const isPulse = brand.isPulse;

  /* ── Redirect already-activated devices ── */
  useEffect(() => {
    const check = async () => {
      const key = localStorage.getItem("deviceKey");
      if (!key) return;
      try {
        const { data } = await axios.get(`${API}/device/status/${key}`);
        if (data.status === "active" || data.status === "trial") {
          try {
            const pls = await axios.get(`${API}/playlist/${key}`);
            navigate(Array.isArray(pls.data) && pls.data.length > 0 ? "/menu" : "/welcome");
          } catch {
            navigate("/menu");
          }
        }
      } catch {}
    };
    check();
  }, [navigate]);

  const features = [
    { icon: Tv,     title: "Live TV",          desc: "Thousands of live channels worldwide" },
    { icon: Play,   title: "Movies & Series",  desc: "Stream VOD content on demand" },
    { icon: Shield, title: "7-Day Trial",       desc: "Free trial, no credit card required" },
    { icon: Globe,  title: "Multi-Device",      desc: "Works on any device, anywhere" },
  ];

  const steps = [
    { num: "01", title: "Open the app",       desc: "You receive a unique device key automatically" },
    { num: "02", title: "Add your playlist",  desc: "Paste your Xtream credentials or M3U URL" },
    { num: "03", title: "Start watching",     desc: "Enjoy a free 7-day trial right away" },
  ];

  const logoBase = process.env.PUBLIC_URL || "";

  /* ── Shared inline helpers ── */
  const cardStyle = {
    background: `${brand.accent}06`,
    border: `1px solid ${brand.accent}20`,
    ...(isPulse && { boxShadow: `0 0 24px ${brand.accent}0a` }),
  };
  const cardHover = (el, enter) => {
    el.style.border = enter ? `1px solid ${brand.accent}50` : `1px solid ${brand.accent}20`;
    if (isPulse) el.style.boxShadow = enter ? `0 0 32px ${brand.accent}22` : `0 0 24px ${brand.accent}0a`;
  };

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: brand.bg }}>

      {/* ══ HERO ══════════════════════════════════════════════════ */}
      <div
        className="relative overflow-hidden"
        style={{
          backgroundColor: brand.bg,
          ...(brand.bgGradient && { backgroundImage: brand.bgGradient }),
        }}
      >
        {isArc   && <ArcDecoration />}
        {isPulse && <PulseDecoration />}

        {/* NAV */}
        <nav className="relative z-10 flex items-center justify-between p-4 sm:p-6 max-w-7xl mx-auto">
          <div className="flex items-center gap-2">
            <img
              src={`${logoBase}/${brand.logoNav}`}
              alt={brand.logoAlt}
              className="h-8 sm:h-10 object-contain"
              onError={(e) => { e.target.style.display = "none"; }}
            />
            {brand.showNameBesideLogo && (
              <span className="font-bold text-lg hidden sm:block" style={{ color: brand.accent }}>
                {brand.name}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => navigate("/welcome")}
              className="text-white/70 hover:text-white text-sm"
              data-testid="nav-activate-button"
            >
              Activate Device
            </Button>
            <Button
              onClick={() => navigate("/admin")}
              variant="outline"
              className="text-sm hover:bg-white/5"
              style={{ borderColor: `${brand.accent}50`, color: brand.accent }}
              data-testid="nav-admin-button"
            >
              Admin
            </Button>
          </div>
        </nav>

        {/* HERO BODY */}
        <div className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-32 text-center">

          {/* Badge */}
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-8"
            style={{ background: `${brand.accent}15`, border: `1px solid ${brand.accent}40` }}
          >
            <Zap className="w-4 h-4" style={{ color: brand.accent }} />
            <span className="text-sm" style={{ color: brand.accent }}>{brand.badgeText}</span>
          </div>

          {/* Logo / Wordmark */}
          <div className="mb-6 flex items-center justify-center">
            <img
              src={`${logoBase}/${brand.logoHero}`}
              alt={brand.logoAlt}
              className={`${brand.heroLogoClass} object-contain mx-auto`}
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>

          {/* Tagline */}
          {isPulse ? (
            <h1
              className="text-3xl md:text-5xl font-bold mb-5"
              style={{
                background: "linear-gradient(135deg, #00E5FF 0%, #A855F7 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              {brand.tagline}
            </h1>
          ) : (
            <h1 className="text-3xl md:text-4xl font-semibold mb-5 text-white/90">
              {brand.tagline}
            </h1>
          )}

          <p className="text-lg text-white/55 mb-10 max-w-2xl mx-auto">{brand.subTagline}</p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              onClick={() => navigate("/welcome")}
              size="lg"
              className="h-14 px-8 text-lg font-medium border-0"
              style={brand.buttonStyle}
              data-testid="hero-get-started-button"
            >
              Get Started <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => document.getElementById("how-it-works")?.scrollIntoView({ behavior: "smooth" })}
              className="h-14 px-8 text-lg border-white/20 rounded-lg hover:bg-white/5"
            >
              How It Works
            </Button>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-10 sm:gap-16 mt-16 pt-16 border-t border-white/10">
            {[
              { val: "10K+",  label: "Live Channels" },
              { val: "50K+",  label: "Movies & Series" },
              { val: "99.9%", label: "Uptime" },
            ].map(({ val, label }) => (
              <div key={label}>
                <p className="text-3xl font-bold" style={{ color: brand.statColor }}>{val}</p>
                <p className="text-white/45 text-sm mt-1">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══ FEATURES ══════════════════════════════════════════════ */}
      <section className="py-24" style={{ backgroundColor: brand.bg }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">Everything You Need</h2>
            <p className="text-white/45 max-w-xl mx-auto">
              {isArc
                ? "Precision-engineered streaming, zero compromise"
                : isPulse
                ? "Streaming built for maximum energy and immersion"
                : "Premium IPTV with all the features you expect"}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {features.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="p-6 rounded-xl transition-all duration-200 cursor-default"
                style={cardStyle}
                onMouseEnter={(e) => cardHover(e.currentTarget, true)}
                onMouseLeave={(e) => cardHover(e.currentTarget, false)}
              >
                <div
                  className="w-12 h-12 rounded-xl flex items-center justify-center mb-4"
                  style={{ background: `${brand.accent}12` }}
                >
                  <Icon className="w-6 h-6" style={{ color: brand.accent }} />
                </div>
                <h3 className="text-base font-semibold mb-1">{title}</h3>
                <p className="text-white/45 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ HOW IT WORKS ══════════════════════════════════════════ */}
      <section id="how-it-works" className="py-24" style={{ backgroundColor: brand.bg }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-3">How It Works</h2>
            <p className="text-white/45 max-w-xl mx-auto">Get started in 3 simple steps</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map(({ num, title, desc }) => (
              <div key={num} className="relative">
                <div
                  className="text-7xl font-bold absolute -top-4 left-0 select-none"
                  style={{ color: `${brand.accent}14` }}
                >
                  {num}
                </div>
                <div className="relative pt-8">
                  <h3 className="text-xl font-semibold mb-2">{title}</h3>
                  <p className="text-white/45">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CTA BANNER ════════════════════════════════════════════ */}
      <section className="py-24" style={{ backgroundColor: brand.bg }}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div
            className="p-10 sm:p-14 rounded-3xl"
            style={{
              background: `linear-gradient(180deg, ${brand.accent}0e 0%, transparent 100%)`,
              border: `1px solid ${brand.accent}28`,
              ...(isPulse && {
                boxShadow: `0 0 80px ${brand.accent}0e, 0 0 160px rgba(168,85,247,0.06)`,
              }),
            }}
          >
            <Clock className="w-11 h-11 mx-auto mb-5" style={{ color: brand.accent }} />
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Start Your 7-Day Free Trial
            </h2>
            <p className="text-white/45 mb-8 max-w-lg mx-auto">
              No credit card required. Get your device key and start watching today.
            </p>
            <Button
              onClick={() => navigate("/welcome")}
              size="lg"
              className="h-14 px-10 text-lg font-medium border-0"
              style={brand.buttonStyle}
              data-testid="cta-activate-button"
            >
              Activate Your Device <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ════════════════════════════════════════════════ */}
      <footer className="py-12 border-t border-white/10" style={{ backgroundColor: brand.bg }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img
                src={`${logoBase}/${brand.logoNav}`}
                alt={brand.logoAlt}
                className="h-6 object-contain"
                onError={(e) => { e.target.style.display = "none"; }}
              />
              {brand.showNameBesideLogo && (
                <span className="font-semibold text-sm" style={{ color: brand.accent }}>
                  {brand.name}
                </span>
              )}
            </div>
            <p className="text-white/35 text-sm">© 2026 {brand.name}. All rights reserved.</p>
            <div className="flex items-center gap-6 text-sm text-white/35">
              <button onClick={() => navigate("/admin")} className="hover:text-white transition-colors">
                Admin
              </button>
              <button onClick={() => navigate("/welcome")} className="hover:text-white transition-colors">
                Activate
              </button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
